import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MovieCard from '@/components/base/MovieCard';
import { categories, movies, getHotMovies, getNewMovies } from '@/mocks/movies';

const heroMovies = movies.filter((m) => m.isHot).slice(0, 4);

const faqData = [
  {
    question: '91电影视频是否需要注册才能观看？',
    answer: '不需要。91电影视频完全免费，无需注册即可畅享海量电影、电视剧、综艺节目，即点即播，无任何限制。',
  },
  {
    question: '视频播放为什么会卡顿或缓冲？',
    answer: '91电影视频采用先进的CDN加速技术，支持高清流畅播放。如遇卡顿，建议检查您的网络连接状态，或尝试切换清晰度以获得更流畅的观看体验。',
  },
  {
    question: '网站上的影片多久更新一次？',
    answer: '我们每日更新最新剧集和电影资源，确保您能第一时间观看到最新的影视内容。热门剧集通常在播出后数小时内即可上线。',
  },
  {
    question: '支持哪些设备观看？',
    answer: '91电影视频完美适配电脑、平板和手机端，无论您使用什么设备，都能获得优质的观影体验。支持主流浏览器如Chrome、Safari、Edge等。',
  },
  {
    question: '影片的画质如何？',
    answer: '我们提供从标清到超清多种画质选择，大部分影片支持1080P高清画质，让您足不出户享受影院级观影体验。',
  },
];

export default function Home() {
  const [currentHero, setCurrentHero] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHero((prev) => (prev + 1) % heroMovies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const hotMovies = getHotMovies().slice(0, 8);
  const newMovies = getNewMovies().slice(0, 8);

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[500px] md:h-[650px] overflow-hidden">
            {heroMovies.map((movie, idx) => (
              <div
                key={movie.id}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  idx === currentHero ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={movie.image}
                  alt={movie.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-background-950/40 via-background-950/20 to-background-950"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-background-950/60 to-transparent"></div>
              </div>
            ))}

            {/* Hero content */}
            <div className="absolute inset-0 flex items-center">
              <div className="w-full px-4 md:px-6 lg:px-10">
                <div className="max-w-xl">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    {heroMovies[currentHero].tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500/20 text-primary-300 border border-primary-500/30"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-50 mb-3">
                    {heroMovies[currentHero].title}
                  </h1>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6 line-clamp-2">
                    {heroMovies[currentHero].description}
                  </p>
                  <div className="flex items-center gap-4 mb-8">
                    <span className="flex items-center gap-1.5 text-sm text-foreground-300">
                      <i className="ri-star-fill text-primary-400 text-base"></i>
                      <strong>{heroMovies[currentHero].rating}</strong>
                    </span>
                    <span className="text-sm text-foreground-500">
                      {heroMovies[currentHero].duration}
                    </span>
                    <span className="text-sm text-foreground-500">
                      {heroMovies[currentHero].region}
                    </span>
                  </div>
                  <Link
                    to={`/detail/${heroMovies[currentHero].id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold rounded-lg text-sm whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    立即观看
                  </Link>
                </div>
              </div>
            </div>

            {/* Hero indicator dots */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
              {heroMovies.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentHero(idx)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    idx === currentHero
                      ? 'bg-primary-500 w-6'
                      : 'bg-foreground-500/50 hover:bg-foreground-400'
                  }`}
                  aria-label={`切换到第${idx + 1}张`}
                />
              ))}
            </div>
          </section>

          {/* Category Navigation */}
          <section className="w-full px-4 md:px-6 py-8">
            <div className="flex items-center gap-3 flex-wrap justify-center">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-6 py-3 bg-background-800 hover:bg-background-700 rounded-xl text-foreground-300 hover:text-foreground-50 transition-all cursor-pointer border border-background-700 hover:border-primary-500/30"
                >
                  <i className={`${cat.icon} text-lg text-primary-400`}></i>
                  <span className="text-sm font-medium whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </section>

          {/* Hot Movies */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">最受欢迎的影片，大家都在看</p>
              </div>
              <Link
                to="/category/movie"
                className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} variant="default" />
              ))}
            </div>
          </section>

          {/* New Updates */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-900/50">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">实时更新，精彩不错过</p>
              </div>
              <Link
                to="/category/tv"
                className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {newMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} variant="default" />
              ))}
            </div>
          </section>

          {/* Category sections */}
          {categories.map((cat) => {
            const catMovies = movies
              .filter((m) => m.category === cat.slug)
              .slice(0, 5);
            if (catMovies.length === 0) return null;
            return (
              <section key={cat.slug} className="w-full px-4 md:px-6 py-10 md:py-14">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-500/15 rounded-xl">
                      <i className={`${cat.icon} text-lg text-primary-400`}></i>
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                        热门{cat.name}
                      </h2>
                      <p className="text-sm text-foreground-500 mt-1">精选{cat.name}内容</p>
                    </div>
                  </div>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
                  >
                    查看全部 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                  {catMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} variant="default" />
                  ))}
                </div>
              </section>
            );
          })}

          {/* Features */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-900/50">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-3">
                为什么选择91电影视频？
              </h2>
              <p className="text-foreground-500">极致观影体验，从这里开始</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {[
                { icon: 'ri-flashlight-line', title: '即点即播', desc: '无需等待，点击即可开始观看，告别漫长的加载和缓冲时间。' },
                { icon: 'ri-hd-line', title: '高清画质', desc: '支持1080P超清画质，带来沉浸式观影体验，画面细节纤毫毕现。' },
                { icon: 'ri-refresh-line', title: '每日更新', desc: '最新电影、热播剧集每日更新，精彩内容永不间断，追剧零延迟。' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '电脑、平板、手机完美适配，随时随地畅享观影乐趣。' },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="p-6 bg-background-800 rounded-xl border border-background-700/50 hover:border-primary-500/20 transition-colors"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-500/10 rounded-xl mb-4">
                    <i className={`${feature.icon} text-xl text-primary-400`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-200 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-3">
                  常见问题
                </h2>
                <p className="text-foreground-500">关于91电影视频的常见疑问解答</p>
              </div>
              <div className="space-y-3">
                {faqData.map((faq, idx) => (
                  <details
                    key={idx}
                    className="group bg-background-800 rounded-xl border border-background-700/50 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-700/30 transition-colors">
                      <span className="text-sm font-medium text-foreground-200 pr-4">
                        {faq.question}
                      </span>
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform text-base flex-shrink-0"></i>
                    </summary>
                    <div className="px-6 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '91电影视频',
            url: 'https://91movie.com',
            description: '91电影视频提供海量免费电影、电视剧、综艺在线观看，无需注册，即点即播，高清不缓冲，每日更新最新剧集。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://91movie.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
            publisher: {
              '@type': 'Organization',
              name: '91电影视频',
              url: 'https://91movie.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '91电影视频',
            url: 'https://91movie.com',
            description: '91电影视频 - 91影视看片免费·91免费在线视频，提供海量免费电影、电视剧、综艺在线观看。',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'contact@91movie.com',
              contactType: 'customer service',
              availableLanguage: ['Chinese'],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqData.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}